import {useAui} from '@assistant-ui/react';
import {useEffect} from 'react';

import {useViewer} from '../../state/viewer/hooks';
import {selectActiveThreadId} from '../chat/store';
import {
  useActiveThread,
  useActiveThreadLoadState,
  useAgentChatStore,
  useIsOnNewChat,
} from '../chat/store/context';
import {waitForThreadLoad} from '../chat/waitForThreadLoad';
import {useWBAgentWindowStore} from './window/useWBAgentWindowStore';

/**
 * WBAgentSessionRestore reopens the persisted committed thread on a full page
 * load and, while the panel is open, keeps `restoreTarget` on the committed
 * thread on screen, stamped with the viewer. The panel opens only once the
 * restored thread has loaded. The restore leaves a failed load on a fresh
 * draft, and drops the target when the thread is missing or belongs to another
 * viewer. Renders nothing.
 *
 * NOTE: A component, not a hook, so it can be mounted only when the agent is
 * available; its `useAui()` call would throw without AuiProvider.
 */
type WBAgentSessionRestoreProps = {
  onRestore?: () => void;
  /**
   * Skip the one-shot restore on mount because the host selects the initial
   * thread itself (the Forge header session mounted on the full-page route).
   * Read once at mount; the `restoreTarget` sync below still runs.
   */
  skipRestore?: boolean;
};

export const WBAgentSessionRestore = ({
  onRestore,
  skipRestore = false,
}: WBAgentSessionRestoreProps) => {
  const aui = useAui();
  const store = useAgentChatStore();
  const threadId = useActiveThread()?.id ?? null;
  const {isLoading} = useActiveThreadLoadState();
  const isOnNewChat = useIsOnNewChat();
  const viewerId = useViewer()?.id ?? null;
  const open = useWBAgentWindowStore(s => s.open);
  const setOpen = useWBAgentWindowStore(s => s.setOpen);
  const setRestoreTarget = useWBAgentWindowStore(s => s.setRestoreTarget);

  // Restore once on mount.
  useEffect(() => {
    const target = skipRestore
      ? null
      : useWBAgentWindowStore.getState().restoreTarget;
    if (target == null) {
      return;
    }
    // NOTE: Drop a target attributed to a different viewer without a request.
    // It can never resolve for this one.
    if (target.viewerId !== viewerId) {
      setRestoreTarget(null);
      return;
    }

    onRestore?.();
    const restore = async () => {
      void aui.threads().switchToThread(target.threadId);
      const outcome = await waitForThreadLoad(store, target.threadId);
      if (outcome == null) {
        return;
      }
      const isActive =
        selectActiveThreadId(store.getState()) === target.threadId;
      if (outcome.status === 'failed') {
        // NOTE: A failed load leaves the chat on the failed thread, so move to a
        // fresh draft. Drop the target only for a missing thread, so a reload
        // retries an error.
        if (outcome.failure.kind === 'not-found') {
          setRestoreTarget(null);
        }
        if (isActive) {
          aui.threads().switchToNewThread();
        }
        return;
      }

      const currentTarget = useWBAgentWindowStore.getState().restoreTarget;
      if (
        isActive &&
        currentTarget?.threadId === target.threadId &&
        currentTarget.viewerId === target.viewerId
      ) {
        setOpen(true);
      }
    };
    void restore();
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Sync `restoreTarget` to the shown committed thread while the panel is
  // open. Closing the panel clears it in the store.
  useEffect(() => {
    // NOTE: While a switch loads, the thread isn't in the store yet, so a write
    // here would persist a transient null and lose the restore target.
    if (!open || isLoading) {
      return;
    }
    // Persist null for a draft. Its id resolves to nothing on reload.
    setRestoreTarget(
      !isOnNewChat && threadId != null && viewerId != null
        ? {threadId, viewerId}
        : null
    );
  }, [open, threadId, isLoading, isOnNewChat, viewerId, setRestoreTarget]);

  return null;
};
